import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
// Service
import { ViewsService } from '../../../../../services/views.service';
import { LayoutRenderService } from '../../../services/layout-render.service';
import { ExternalApiService } from '../../../../services/external-api.service';
import { BroadcastService, ModalService, ToasterType } from '@cv-invision/core';
// Model
import { DynamicInterface } from '../../../../models/dynamic.interface';
import { LayoutRenderLayoutItem } from '../../../models/cv-layout-render.model';
import { TabsEventType } from '../tabs-section/model/tabs-section.event';
import { GeneralEventType } from '../../models/component.events';
// Component
import { Lightbox } from 'ngx-lightbox';

@Component({
	selector: 'cv-schema-render-photo-list-bulk-delete',
	templateUrl: './photo-list-bulk-delete.component.html'
})
export class PhotoListBulkDeleteComponent implements OnInit, OnDestroy, DynamicInterface {
	@Input() data: any;
	@Input() layoutNode = new LayoutRenderLayoutItem();

	markForCheck: boolean;
	thisData: any;
	photos: any;
	options: any;
	path: string | null;
	public album = [];
	public selectedPhotos: any = [];

	get order(): any {
		return this.viewsService.order;
	}

	get flattenedPhotos(): any {
		return this.viewsService.flattenedPhotos;
	}

	get isEditable(): boolean {
		return this.layoutRenderService.extraData.isEditing;
	}

	protected atComponentDestroy = new Subject();

	constructor(
		private modalService: ModalService,
		private viewsService: ViewsService,
		private broadcastService: BroadcastService,
		private layoutRenderService: LayoutRenderService,
		private externalApiService: ExternalApiService,
		private lightbox: Lightbox
	) {
	}

	async ngOnInit(): Promise<void> {
		await this.initializeComponent();
	}

	ngOnDestroy(): void {
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}

	/**
	 * Initialice component
	 */
	async initializeComponent(): Promise<void> {
		this.options = { ...this.options, ...this.layoutNode.options };

		if (this.options.photosInData) {
			this.photos = this.data.photos;
			this.thisData = this.data.data;
		} else {
			// calculate photos given the path in data
			this.thisData = this.data.data;
		}

		this.path = this.thisData._Path;

		if (this.path) {
			await this.loadImages();
		}
	}

	/**
	 * Wait Changes to the Component if the Component was Mark For Check
	 *
	 * @param changes
	 */
	public async reloadData(init: any): Promise<void> {
		this.data = init.data;
		this.layoutNode = init.layoutNode;

		await this.initializeComponent();
	}

	/**
	 * initialize the image variable with the images for certain section.
	 */
	async loadImages(): Promise<void> {
		this.album = [];

		if (this.photos && this.photos.length) {
			this.photos.forEach(img => {
				const image = {
					id: img.id,
					src: img.path,
					thumb: img.thumbnails[0],
					description: img.metadata.description || ''
				};
				this.selectedPhotos.push(img.id);
				this.album.push(image);
			});
		}
	}

	/**
	 * Open lightbox
	 * @param index
	 */
	openGallery(index: any): void {
		this.lightbox.open(this.album, index);
	}

	/**
	 * doc
	 * @param index
	 */
	selectPhoto(index: any): void {
		const photoSelected = this.album[index];

		if (this.selectedPhotos.indexOf(photoSelected.id) > -1) {
			this.selectedPhotos.splice(this.selectedPhotos.indexOf(photoSelected.id), 1);
		} else {
			this.selectedPhotos.push(photoSelected.id);
		}
	}

	/**
	 * Show user confirmation before deleting photos
	 */
	deletePhotos(): void {
		this.modalService
			.customConfirm(
				'Delete Photos',
				`Do you really want to proceed with the bulk deletion of photos? There is no undo operation.`,
				'icon-warning fas fa-exclamation-circle',
				'Delete',
				'cv-btn-link-error'
			)
			.subscribe(async result => {
				if (result.confirm === 'confirm') {
					await this.deleteImagesFromGallery(this.selectedPhotos);
				}
			});
	}

	/**
	 * After user confirmation, delete photos from server & memory
	 * @param photos
	 */
	async deleteImagesFromGallery(photos: any[]): Promise<void> {
		const response = await this.externalApiService.deleteAttachmentsBulk(this.order.id, photos).toPromise();
		const toastData = {
			type: 'error',
			title: 'Error',
			message: 'Unable to delete photos'
		};

		if (response && response !== null) {
			for (let i = 0; i < photos.length; i++) {
				const photoSelectedId = photos[i];
				const photoSelectedObj = this.photos.find((attachment: any) => { return attachment.id === photoSelectedId; });

				const albumPhotosIndex = this.album.findIndex((attachment: any) => {
					return attachment.id === photoSelectedId;
				});

				if (this.album.length >= 0) {
					this.album.splice(albumPhotosIndex, 1);
				}

				const flattenedPhotosIndex = this.viewsService.flattenedPhotos[photoSelectedObj.refJsonPath].findIndex((attachment: any) => {
					return attachment.id === photoSelectedId;
				});
				if (flattenedPhotosIndex > -1) {
					this.viewsService.flattenedPhotos[photoSelectedObj.refJsonPath].splice(flattenedPhotosIndex, 1);
				}

				// Delete it from View Services Photos
				const viewServicePhotosIndex = this.viewsService.photos.findIndex((attachment: any) => {
					return attachment.id === photoSelectedId;
				});
				if (viewServicePhotosIndex > -1) {
					this.viewsService.photos.splice(viewServicePhotosIndex, 1);
				}
			}

			if (this.album.length === 0) {
				this.layoutRenderService.componentBroadcast.next({
					eventType: TabsEventType.GET_ACTIVE_TAB,
					jsonPath: this.path,
					metaData: {
						reloadData: true
					}
				});
			}

			this.layoutRenderService.componentBroadcast.next({
				eventType: GeneralEventType.UPDATE_DATA,
				jsonPath: this.path,
				metaData: response
			});


			toastData.type = 'warning';
			toastData.title = 'Images deleted';
			toastData.message = 'All the images you selected were successfully deleted.';
		}

		this.broadcastService.broadcast({
			name: ToasterType.TOASTER,
			event: toastData
		});
	}

}
